import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Icon,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { ImTicket } from "react-icons/im";

export interface ShowNoteCardLegendItem {
  label: string;
  color: string;
}

export interface ShowNoteCardComponent1Props {
  containerProps?: BoxProps;
  // Note card
  noteCardProps?: FlexProps;
  iconBoxProps?: FlexProps;
  noteEnglishProps?: BoxProps;
  noteLabelProps?: TextProps;
  noteText?: string;
  noteTextProps?: TextProps;
  dividerProps?: BoxProps;
  noteNepaliProps?: BoxProps;
  nepaliLabelProps?: TextProps;
  nepaliNoteText?: string;
  nepaliTextProps?: TextProps;
  // Seat legend
  legendContainerProps?: FlexProps;
  legendItems?: ShowNoteCardLegendItem[];
  legendItemProps?: FlexProps;
  legendDotProps?: BoxProps;
  legendTextProps?: TextProps;
}

export function ShowNoteCardComponent1({
  containerProps,
  noteCardProps,
  iconBoxProps,
  noteEnglishProps,
  noteLabelProps,
  noteText,
  noteTextProps,
  dividerProps,
  noteNepaliProps,
  nepaliLabelProps,
  nepaliNoteText,
  nepaliTextProps,
  legendContainerProps,
  legendItems,
  legendItemProps,
  legendDotProps,
  legendTextProps,
}: ShowNoteCardComponent1Props) {
  const hasNote = !!(noteText || nepaliNoteText);
  const hasLegend = !!(legendItems && legendItems.length > 0);

  if (!hasNote && !hasLegend) return null;

  return (
    <Box {...containerProps}>
      {hasNote && (
        <Flex
          bg="white"
          borderLeft="4px solid"
          borderColor="primary.500"
          borderRadius="8px"
          p={{ base: "14px 16px", md: "22px 28px" }}
          align={{ base: "flex-start", md: "center" }}
          gap={{ base: "12px", md: "16px" }}
          boxShadow="0 2px 8px rgba(0,0,0,0.06)"
          mb={{ base: 4, md: 5 }}
          direction={{ base: "column", sm: "row" }}
          {...noteCardProps}
        >
          {/* Amber icon circle */}
          <Flex
            bg="primary.500"
            borderRadius="full"
            w={{ base: "40px", md: "46px" }}
            h={{ base: "40px", md: "46px" }}
            align="center"
            justify="center"
            flexShrink={0}
            color="white"
            fontSize={{ base: "18px", md: "21px" }}
            {...iconBoxProps}
          >
            <Icon as={ImTicket} />
          </Flex>

          {/* English + Nepali columns */}
          <Flex
            flex="1"
            minW="0"
            align="stretch"
            gap={{ base: "10px", md: "16px" }}
            direction={{ base: "column", md: "row" }}
          >
            {/* English */}
            <Box flex="1" minW="0" {...noteEnglishProps}>
              <Text
                fontSize="10px"
                fontWeight="700"
                letterSpacing="wider"
                color="gray.500"
                textTransform="uppercase"
                mb="4px"
                {...noteLabelProps}
              >
                NOTE
              </Text>
              {noteText && (
                <Text
                  fontSize={{ base: "12px", md: "13px" }}
                  color="gray.800"
                  lineHeight="1.65"
                  {...noteTextProps}
                >
                  {noteText}
                </Text>
              )}
            </Box>

            {/* Vertical divider — desktop only */}
            <Box
              display={{ base: "none", md: "block" }}
              w="1px"
              alignSelf="stretch"
              bg="gray.200"
              flexShrink={0}
              {...dividerProps}
            />

            {/* Nepali */}
            <Box flex="1" minW="0" {...noteNepaliProps}>
              <Text
                fontSize="10px"
                fontWeight="700"
                letterSpacing="wider"
                color="gray.500"
                textTransform="uppercase"
                mb="4px"
                {...nepaliLabelProps}
              >
                सूचना
              </Text>
              {nepaliNoteText && (
                <Text
                  fontSize={{ base: "12px", md: "13px" }}
                  color="gray.800"
                  lineHeight="1.65"
                  {...nepaliTextProps}
                >
                  {nepaliNoteText}
                </Text>
              )}
            </Box>
          </Flex>
        </Flex>
      )}

      {/* Seat availability legend */}
      {hasLegend && (
        <Flex
          justify="center"
          gap={{ base: "12px", md: "24px" }}
          flexWrap="wrap"
          mb={{ base: 4, md: 5 }}
          {...legendContainerProps}
        >
          {legendItems!.map(({ label, color }) => (
            <Flex key={label} align="center" gap="6px" {...legendItemProps}>
              <Box
                w="14px"
                h="14px"
                borderRadius="3px"
                bg={color}
                flexShrink={0}
                {...legendDotProps}
              />
              <Text
                fontSize={{ base: "12px", md: "13px" }}
                color="gray.700"
                {...legendTextProps}
              >
                {label}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  );
}

export default ShowNoteCardComponent1;
